/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import isEmpty from 'lodash.isempty';
import useCurrentGlobalGroupId from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import useUrl from '@ion/applications/src/hooks/useUrl';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {translate} from '@ion/core/src/locale/locale';
import {getJob} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Job} from '@ion/core/src/types/entities';
import {changeURLPath} from '@ion/core/src/utils/BrowserUtils';
import {setGlobalGroup} from '@/store/actions/global-group-chooser';

// Enum defined for the identifiers which supports Job shareable link.
enum Identifiers {
  JOBID = 'jobid'
}

// Enum defined for the identifiers terms to show in error message.
enum IdentifierTerms {
  JOBID = 'Job ID'
}

// Array of identifiers which supports viewing job details using shareable link.
export const ids = [
  Identifiers.JOBID
];

/**
 * useJobDeepLink hook used to open job tab view based on
 * url params
 *
 * ```typescript
 *
 * import {useJobDeepLink} from '@ion/applications/src/hooks/';
 *
 * const jobId = useJobDeepLink();
 * ```
 */
export const useJobDeepLink = ({handleAddTab}) => {
  const dispatch  = useDispatch();
  const accountId = useCurrentGlobalGroupId();
  
  const {
    hasQueryParam,
    getQueryParams,
    getPath
  }                   = useUrl();
  const params        = getQueryParams();
  const identifier    = !isEmpty(params) ? ids.filter(id => hasQueryParam(id))?.[0] || '' : '';
  const idValue       = params[identifier];

  useEffect(() => {
    if (!isEmpty(idValue)) {
      getJobId(idValue);
    }
  }, [idValue]);

  const showError = useCallback((notFound: boolean) => {
    changeURLPath(getPath());
    dispatch(toggleUserPrompt({
      autoHide    : false,
      actionLabel : translate('common:dismiss'),
      message     : notFound ? translate('jobsearchengine:viewJob.notFound', {identifier: IdentifierTerms[identifier.toUpperCase()], idValue}) : translate('jobsearchengine:viewJob.jobError')
    }));
  }, [identifier, idValue]);

  const getJobId = useCallback(async(idValue) => {
    try {
      const response = await getJob(idValue) as Job;
      if (response) {
        if (accountId !== entityId(response.account)) {
          dispatch(setGlobalGroup(response.account) as any);
        }
        handleAddTab(idValue, response?.progressStatus);
      }
    } catch (err) {
      showError(err?.status === 404);
    }
  }, [identifier]);

  return idValue;
};

export default useJobDeepLink;
